import { lazy, ComponentType, LazyExoticComponent } from 'react';

export interface RouteConfig {
  path: string;
  name: string;
  priority: number;
  changefreq: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
  includeInSitemap: boolean;
  component: LazyExoticComponent<ComponentType<any>>;
}

// Lazy load page components for code splitting
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const MentalHealthIndex = lazy(() => import('@/pages/MentalHealthIndex'));
const RegionalAnalysis = lazy(() => import('@/pages/RegionalAnalysis'));
const MemberInsights = lazy(() => import('@/pages/MemberInsights'));
const ProgramsEvents = lazy(() => import('@/pages/ProgramsEvents'));
const TechnologyFuture = lazy(() => import('@/pages/TechnologyFuture'));
const ImpactMetrics = lazy(() => import('@/pages/ImpactMetrics'));
const Vision2040 = lazy(() => import('@/pages/Vision2040'));
const WorkforceEducation = lazy(() => import('@/pages/WorkforceEducation'));
const EntrepreneurshipSupport = lazy(() => import('@/pages/EntrepreneurshipSupport'));
const Settings = lazy(() => import('@/pages/Settings'));
const TranslationDebug = lazy(() => import('@/pages/TranslationDebug'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Contributing = lazy(() => import('@/pages/Contributing'));
const FAQ = lazy(() => import('@/pages/FAQ'));
const Methodology = lazy(() => import('@/pages/Methodology'));
const DevelopmentCouncils = lazy(() => import('@/pages/DevelopmentCouncils'));
const ExecutiveBrief = lazy(() => import('@/pages/ExecutiveBrief'));
const WhitePaper = lazy(() => import('@/pages/WhitePaper'));
const AdminErrorLogs = lazy(() => import('@/pages/AdminErrorLogs'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const CountryProfile = lazy(() => import('@/pages/CountryProfile'));
const ChatJCIPage = lazy(() => import('@/pages/ChatJCIPage'));
const SharedConversation = lazy(() => import('@/pages/SharedConversation'));
const ApiDocs = lazy(() => import('@/pages/ApiDocs'));

export const routes: RouteConfig[] = [
  {
    path: '/dashboard',
    name: 'Dashboard',
    priority: 1.0,
    changefreq: 'weekly',
    includeInSitemap: true,
    component: Dashboard,
  },
  {
    path: '/methodology',
    name: 'Methodology',
    priority: 0.9,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: Methodology,
  },
  {
    path: '/impact-metrics',
    name: 'Impact & Metrics',
    priority: 0.9,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: ImpactMetrics,
  },
  {
    path: '/mental-health-dimension',
    name: 'Mental Health Dimension',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: MentalHealthIndex,
  },
  {
    path: '/workforce-education',
    name: 'Workforce & Education',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: WorkforceEducation,
  },
  {
    path: '/entrepreneurship-support',
    name: 'Entrepreneurship Support',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: EntrepreneurshipSupport,
  },
  {
    path: '/regional-analysis',
    name: 'Regional Analysis',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: RegionalAnalysis,
  },
  {
    path: '/member-insights',
    name: 'Member Insights',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: MemberInsights,
  },
  {
    path: '/programs-events',
    name: 'Programs & Events',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: ProgramsEvents,
  },
  {
    path: '/technology-future',
    name: 'Technology & Future',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: TechnologyFuture,
  },
  {
    path: '/vision-2040',
    name: 'Vision 2040',
    priority: 0.9,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: Vision2040,
  },
  {
    path: '/settings',
    name: 'Settings',
    priority: 0.3,
    changefreq: 'monthly',
    includeInSitemap: false,
    component: Settings,
  },
  {
    path: '/translation-debug',
    name: 'Translation Debug',
    priority: 0.1,
    changefreq: 'never',
    includeInSitemap: false,
    component: TranslationDebug,
  },
  {
    path: '/contributing',
    name: 'Contributing',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: Contributing,
  },
  {
    path: '/faq',
    name: 'FAQ',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: FAQ,
  },
  {
    path: '/development-councils',
    name: 'Development Councils',
    priority: 0.8,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: DevelopmentCouncils,
  },
  {
    path: '/executive-brief',
    name: 'Executive Brief',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: ExecutiveBrief,
  },
  {
    path: '/white-paper',
    name: 'White Paper',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: WhitePaper,
  },
  {
    path: '/admin/error-logs',
    name: 'Error Logs',
    priority: 0.1,
    changefreq: 'never',
    includeInSitemap: false,
    component: AdminErrorLogs,
  },
  {
    path: '/privacy',
    name: 'Privacy Policy',
    priority: 0.5,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: PrivacyPolicy,
  },
  {
    path: '/terms',
    name: 'Terms of Service',
    priority: 0.5,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: TermsOfService,
  },
  {
    path: '/country/:countrySlug',
    name: 'Country Profile',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: false, // Dynamic routes not in sitemap
    component: CountryProfile,
  },
  {
    path: '/chatjci',
    name: 'ChatJCI',
    priority: 0.6,
    changefreq: 'monthly',
    includeInSitemap: false,
    component: ChatJCIPage,
  },
  {
    path: '/chat/shared/:token',
    name: 'Shared Conversation',
    priority: 0.3,
    changefreq: 'never',
    includeInSitemap: false,
    component: SharedConversation,
  },
  {
    path: '/api-docs',
    name: 'API Documentation',
    priority: 0.7,
    changefreq: 'monthly',
    includeInSitemap: true,
    component: ApiDocs,
  },
];

// Special routes not in main navigation
export const notFoundRoute: Omit<RouteConfig, 'priority' | 'changefreq' | 'includeInSitemap'> = {
  path: '*',
  name: 'Not Found',
  component: NotFound,
};

// Sitemap configuration
export const sitemapConfig = {
  baseUrl: 'https://jci-futures-observatory.lovable.app',
  supportedLanguages: ['en', 'pt', 'es', 'fr', 'ja'] as const,
  defaultLanguage: 'en' as const,
};

// Helper to get routes for sitemap
export const getSitemapRoutes = () => routes.filter(route => route.includeInSitemap);
